import { StrictMode } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { CssBaseline } from '@mui/material';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { AuthProvider } from 'react-oidc-context';
import { Provider as ReduxProvider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { StartFlowLocaleReporter } from 'features/auth/components/StartFlowLocaleReporter';
import { AuthProvider as CustomAuthProvider } from 'features/auth/context/AuthProvider';
import { DevAuthProvider } from 'features/auth/context/DevAuthProvider';
import { oidcConfig } from 'features/auth/utils/identityOidcConfig';
import { preconnectSignInHostsAtLoad } from 'features/auth/utils/signInPreconnect';
import { AppLocalizationProvider } from 'features/i18n/DynamicTranslation/AppLocalizationProvider';
import { initializeDynamicI18n } from 'features/i18n/DynamicTranslation/dynamicI18n';
import { DynamicTranslationProvider } from 'features/i18n/DynamicTranslation/DynamicTranslationProvider';
import i18n from 'features/i18n/i18n';
import {
  LoadingOrchestratorProvider,
  syncStartFlowWithPage,
} from 'features/loading';
import { datadogRumAddCustomAction } from 'features/monitoring/customDatadogRumActions';
import { config } from 'features/notifications/config';
import { initProseServiceFactory } from 'features/questionnaires/services/proseServiceFactory';
import { getFirebaseDefaultHostRedirect } from 'features/routing/firebaseDefaultHostRedirect';
import { store } from 'features/store/store';
import { ThemeProvider } from 'features/theme/ThemeProvider';
import 'features/notifications/styles.css';
import { initDatadog } from 'integrations/datadog/datadogRum';
/* eslint-disable import/no-unassigned-import -- the web code of the Capacitor plugins that run at startup loads with the entry chunk, so their first call fetches nothing */
import '@capacitor-firebase/authentication/dist/esm/web';
import '@capacitor/network/dist/esm/web';
import '@capacitor/preferences/dist/esm/web';
/* eslint-enable import/no-unassigned-import -- end of the plugin web code imports */
import { App } from './App';
import { NetworkStatusProvider } from './features/network/NetworkStatusProvider';

const startApp = () => {
  preconnectSignInHostsAtLoad();

  initDatadog();
  datadogRumAddCustomAction({ name: 'appInitialize' });

  initProseServiceFactory(store);

  // Initialize dynamic i18n with the store
  initializeDynamicI18n(store);

  if (Capacitor.isNativePlatform()) {
    // Capacitor App plugin docs claim that appStateChange event should be called in the browser
    // when visibilitychange event is fired but the event was often missing in Chrome during dev while
    // the document event handler functioned correctly...
    void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        datadogRumAddCustomAction({ name: 'appActive' });
      } else {
        datadogRumAddCustomAction({ name: 'appInactive' });
      }
    });
  } else {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        datadogRumAddCustomAction({ name: 'appActive' });
      } else {
        datadogRumAddCustomAction({ name: 'appInactive' });
      }
    });
  }

  const isDevAutoLogin =
    import.meta.env.MODE === 'development' &&
    !!import.meta.env.PUBLIC_DEV_AUTO_LOGIN;

  const appTree = (
    <StrictMode>
      <I18nextProvider i18n={i18n}>
        <DynamicTranslationProvider>
          <AppLocalizationProvider>
            <App />
            <ToastContainer {...config} />
          </AppLocalizationProvider>
        </DynamicTranslationProvider>
      </I18nextProvider>
    </StrictMode>
  );

  syncStartFlowWithPage(window.location);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- root element is always present
  createRoot(document.getElementById('root')!).render(
    <ReduxProvider store={store}>
      <ThemeProvider>
        <CssBaseline />
        <LoadingOrchestratorProvider>
          <NetworkStatusProvider>
            {isDevAutoLogin ? (
              <DevAuthProvider>{appTree}</DevAuthProvider>
            ) : (
              <AuthProvider {...oidcConfig}>
                <StartFlowLocaleReporter />
                <CustomAuthProvider>{appTree}</CustomAuthProvider>
              </AuthProvider>
            )}
          </NetworkStatusProvider>
        </LoadingOrchestratorProvider>
      </ThemeProvider>
    </ReduxProvider>
  );
};

const firebaseDefaultHostRedirect = getFirebaseDefaultHostRedirect(
  window.location,
  import.meta.env.PUBLIC_FIREBASE_PROJECT_ID,
  import.meta.env.PUBLIC_DOMAIN
);

if (firebaseDefaultHostRedirect) {
  window.location.replace(firebaseDefaultHostRedirect);
} else {
  startApp();
}
